/**
 * What the Verification page says at the top, in one place that can be tested.
 *
 * WHY THIS IS NOT INLINE JSX ANY MORE. On 2026-08-26 the page showed a green
 * tick and "All checks are clear." on a study where no check had ever run and a
 * recorded FAIL sat in Firestore. The count it keyed on — open decisions in
 * `validation/latest` and the guide run — is 0 both when everything passed and
 * when nothing was ever examined. Those are opposite states and they were
 * rendered identically, in the most reassuring way available.
 *
 * This repo's standing rule is that a null result is not evidence until the
 * instrument has fired on a known positive. A reader of this page has no
 * instrument at all — only a tick — so the page has to make the distinction
 * for them.
 *
 * `tone` exists so the ICON is decided here too. Wording that says "not run"
 * beside a green tick is still read as a pass; the tick is the part a hurried
 * reader takes away.
 */

export type HeadlineTone = 'clear' | 'attention' | 'neutral';

export interface VerificationHeadline {
  tone: HeadlineTone;
  title: string;
  subtitle: string;
}

export interface HeadlineInput {
  /** Open decisions across the runs this page can see. */
  activeCount: number;
  /** Findings the user has already declined. */
  declinedCount: number;
  /** True when neither run document exists — nothing has been checked. */
  neverRun: boolean;
}

export function verificationHeadline(i: HeadlineInput): VerificationHeadline {
  const active = Number(i?.activeCount) || 0;
  const declined = Number(i?.declinedCount) || 0;

  // Checked FIRST, and before the zero-count branch. A study that has never
  // been examined must never reach the reassuring wording, whatever the counts
  // happen to be.
  if (i?.neverRun) {
    return {
      tone: 'neutral',
      title: 'Verification has not run yet',
      subtitle:
        'No checks have been run on this study, so nothing here says it is correct. ' +
        'Checks run automatically as documents are generated, or press Re-run.',
    };
  }

  if (active === 0) {
    return {
      tone: 'clear',
      title: 'Nothing needs your decision',
      subtitle:
        declined > 0
          ? `${declined} finding${declined === 1 ? '' : 's'} declined.`
          : 'All checks are clear.',
    };
  }

  return {
    tone: 'attention',
    title: `${active} item${active === 1 ? '' : 's'} need${active === 1 ? 's' : ''} your review`,
    subtitle:
      'Review each below — take you there, approve a fix, or decline with a reason.' +
      (declined > 0 ? ` ${declined} already declined.` : ''),
  };
}
